import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  type Block,
  INLINES,
  type Document as RichTextDocument,
  type TopLevelBlock,
} from '@contentful/rich-text-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { ReactNode } from 'react';

import Link from '@/components/Link/Link';
import useGlobalStyles from '@/styles/global.styles';
import useStyles from './RichText.styles';
import { embeddedEntryRenderer } from './renderers/embeds';

interface Props {
  textId?: string;
  title?: string;
  content: RichTextDocument;
  className?: string;
  blockClasses?: {
    p?: string;
    h1?: string;
    h2?: string;
    h3?: string;
    h4?: string;
    h5?: string;
    ul?: string;
    ol?: string;
  };
  isOnDark?: boolean;
}

export interface IndexedTopLevelBlock extends TopLevelBlock {
  index: number;
}

export default function RichText({
  textId,
  title,
  content,
  className,
  blockClasses,
  isOnDark,
}: Props) {
  const { classes, cx } = useStyles({ isOnDark });
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const id = title ? `rc-${title.toLowerCase().replace(/\s+/g, '-')}` : '';
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  const renderOptions: any = {
    // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
    renderText: (text: any) => {
      return (
        text
          .split('\n')
          // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
          .reduce((children: any, textSegment: any, index: any) => {
            // biome-ignore lint/performance/noAccumulatingSpread: Automated migration to Biome
            // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
            return [...children, index > 0 && <br key={index} />, textSegment];
          }, [])
      );
    },
    renderNode: {
      // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
      [INLINES.HYPERLINK]: ({ data }: any, children: any) => {
        return <Link href={data.uri}>{children}</Link>;
      },
      [INLINES.EMBEDDED_ENTRY]: embeddedEntryRenderer,
    },
  };

  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.HEADING_1] = (node: any, children: any) => (
    <h1 className={cx(globalClasses.h1, blockClasses?.h1)}>{children}</h1>
  );
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.HEADING_2] = (node: any, children: any) => {
    let idString: string;
    if (node.index || node.index === 0) {
      idString = `${node.content[0].value?.replace(/\W/g, '_')}_${node.index}`;
    } else {
      idString = node.content[0].value?.replace(/\W/g, '_');
    }
    return (
      <h2 id={idString} className={cx(globalClasses.h2, blockClasses?.h2)}>
        {children}
      </h2>
    );
  };
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.HEADING_3] = (node: any, children: any) => {
    let idString: string;
    if (node.index || node.index === 0) {
      idString = `${node.content[0].value?.replace(/\W/g, '_')}_${node.index}`;
    } else {
      idString = node.content[0].value?.replace(/\W/g, '_');
    }
    return (
      <h3 id={idString} className={cx(globalClasses.h3, blockClasses?.h3)}>
        {children}
      </h3>
    );
  };
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.HEADING_4] = (node: any, children: any) => (
    <h4 className={cx(globalClasses.h4, blockClasses?.h4)}>{children}</h4>
  );
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.HEADING_5] = (node: any, children: any) => (
    <h5 className={cx(globalClasses.h5, blockClasses?.h5)}>{children}</h5>
  );
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.PARAGRAPH] = (node: any, children: any) => (
    <p className={cx(globalClasses.body, blockClasses?.p)}>{children}</p>
  );
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.UL_LIST] = (node: any, children: any) => (
    <ul className={cx(globalClasses.body, blockClasses?.ul)}>{children}</ul>
  );
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
  renderOptions.renderNode[BLOCKS.OL_LIST] = (node: any, children: any) => (
    <ol className={cx(globalClasses.body, blockClasses?.ol)}>{children}</ol>
  );
  renderOptions.renderNode[BLOCKS.TABLE] = (
    node: Block,
    children: ReactNode[]
  ) => {
    const [firstRow] = node.content;

    if (firstRow.nodeType !== 'text') {
      const [firstRowFirstCell] = firstRow.content;

      if (firstRowFirstCell.nodeType === BLOCKS.TABLE_HEADER_CELL) {
        const [header, ...body] = children;

        return (
          <TableContainer>
            <Table>
              <TableHead>{header}</TableHead>
              <TableBody>{body}</TableBody>
            </Table>
          </TableContainer>
        );
      }
    }

    return (
      <TableContainer>
        <Table>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    );
  };
  renderOptions.renderNode[BLOCKS.TABLE_ROW] = (
    _node: Block,
    children: ReactNode
  ) => <TableRow>{children}</TableRow>;
  renderOptions.renderNode[BLOCKS.TABLE_CELL] = (
    _node: Block,
    children: ReactNode
  ) => (
    <TableCell variant='body' className={cx(classes.tableCell)}>
      {children}
    </TableCell>
  );
  renderOptions.renderNode[BLOCKS.TABLE_HEADER_CELL] = (
    _node: Block,
    children: ReactNode
  ) => (
    <TableCell
      variant='head'
      className={cx(classes.tableCell, classes.tableHeaderCell)}
    >
      {children}
    </TableCell>
  );

  return (
    <div
      id={textId ? textId : id}
      className={cx(classes.container, classes.links, className)}
    >
      {documentToReactComponents(content, renderOptions)}
    </div>
  );
}
